import React from "react";
// import SwipeableViews from "react-swipeable-views";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import CssBaseline from "@material-ui/core/CssBaseline";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { createMuiTheme, ThemeProvider } from "@material-ui/core";

import { useDispatch, useSelector } from "react-redux";

// My custom
import * as controller from "./controller";
import useStyles from "./styles";
import MainTabView from "./components/MainTabView";
import LoadingServerView from "./components/LoadingServerView";
import AdvancedOptionsView from "./components/AdvancedOptionsView";
import { RootState } from "./redux/store";
import {
  setIsSpinnerHidden,
  setIsComputing,
  setIsXPSEmpty,
  MeanType,
  setShowZip,
  setIsServerOn,
  setDownloadName,
  setBlobHref,
  setSingleCurveSVG,
  setModels,
  setBasenames,
  setVersion,
  ModelParameters,
  setCurrentModelParameters,
} from "./redux/App.store";
import PlotsView from "./components/PlotsView";
import ModelsView from "./components/ModelsView";

const App = () => {
  const classes = useStyles();

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = React.useMemo(
    () =>
      createMuiTheme({
        palette: {
          type: prefersDarkMode ? "dark" : "light",
        },
      }),
    [prefersDarkMode]
  );

  // States
  const dispatch = useDispatch();
  const [tab, setTab] = React.useState(0);
  const isSpinnerHidden = useSelector(
    (state: RootState) => state.app.isSpinnerHidden
  );
  const xpsfiles = useSelector((state: RootState) => state.app.xpsfiles);
  const isComputing = useSelector((state: RootState) => state.app.isComputing);
  const isServerOn = useSelector((state: RootState) => state.app.isServerOn);
  const isXPSEmpty = useSelector((state: RootState) => state.app.isXPSEmpty);
  const meanType = useSelector((state: RootState) => state.app.meanType);
  const zerosRight = useSelector((state: RootState) => state.app.zerosRight);
  const zerosLeft = useSelector((state: RootState) => state.app.zerosLeft);
  const isLogScale = useSelector((state: RootState) => state.app.isLogScale);
  const multiLabel = useSelector((state: RootState) => state.app.multiLabel);
  const filesList = useSelector((state: RootState) => state.app.filesList);
  const version = useSelector((state: RootState) => state.app.version);
  const isSingleFile = useSelector(
    (state: RootState) => state.app.isSingleFile
  );

  const [email, setEmail] = React.useState("");

  React.useEffect(() => {
    controller.isSeverAlive().then((d) => {
      dispatch(setIsServerOn(true));
      dispatch(setVersion(d.version));
      setEmail(d.email);
    });
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(setIsXPSEmpty(xpsfiles.length < 1));
    dispatch(setShowZip(false));
  }, [xpsfiles, dispatch]);

  // functions

  function handleExecSingle() {
    const options: controller.CommonOptions = {
      meanType: meanType === MeanType.geo ? "geo" : "ari",
      zerosLeft: zerosLeft,
      zerosRight: zerosRight,
      logScale: isLogScale,
      multiLabel: multiLabel,
    };
    controller
      .sendSingleMode(filesList[0], options)
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        console.log(data.models);
        const href = "data:text/plain;base64," + data.zip;
        dispatch(setDownloadName(data.basename + ".zip"));
        dispatch(setDownloadName(data.basename + ".zip"));
        dispatch(setBasenames([data.basename]));
        dispatch(setSingleCurveSVG(data.curves));
        dispatch(setModels(data.models));
        dispatch(setBlobHref(href));
        dispatch(setShowZip(true));
        // create parameters for each model object
        let models_parameters: Array<ModelParameters> = [];

        data.models.forEach((model: ModelParameters) => {
          const model_parameters: ModelParameters = {
            key: model.key,
            model_repr: model.model_repr,
            parameters: model.parameters,
            r_squared: model.r_squared,
            error_mean: model.error_mean,
          };

          models_parameters.push(model_parameters);
        });

        dispatch(setCurrentModelParameters(models_parameters));
      })
      .catch((e) => {
        console.log("Error: " + e);
      })
      .finally(() => {
        dispatch(setIsSpinnerHidden(true));
        dispatch(setIsComputing(false));
      });
  }

  function handleExecMulti() {
    const options: controller.CommonOptions = {
      meanType: meanType === MeanType.geo ? "geo" : "ari",
      zerosLeft: zerosLeft,
      zerosRight: zerosRight,
      logScale: isLogScale,
      multiLabel: multiLabel,
    };
    controller
      .sendMultiMode(filesList, options)
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        const href = "data:text/plain;base64," + data.zip;
        dispatch(setDownloadName(data.basename + ".zip"));
        dispatch(setSingleCurveSVG(data.curves_dist));
        dispatch(setBlobHref(href));
        dispatch(setShowZip(true));
      })
      .catch((e) => {
        console.log("Error: " + e);
      })
      .finally(() => {
        dispatch(setIsSpinnerHidden(true));
        dispatch(setIsComputing(false));
      });
  }

  function handleExec() {
    dispatch(setIsSpinnerHidden(false));
    dispatch(setIsComputing(true));
    dispatch(setShowZip(false));
    if (isSingleFile) {
      return handleExecSingle();
    } else {
      return handleExecMulti();
    }
  }

  // dispatch(setIsServerOn(false));

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBar position="static" color="default" className={classes.appBar}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
            }}
            variant="fullWidth"
          >
            <Tab label="Principal" />
            <Tab label="Opções avançadas" />
            <Tab label="Curvas" disabled={isXPSEmpty} />
            <Tab label="Modelos" disabled={isXPSEmpty || !isSingleFile} />
          </Tabs>
        </AppBar>
        {isServerOn ? (
          <Container
            maxWidth="sm"
            className={classes.mainTab}
            style={{ height: "100vh" }}
          >
            <Grid
              container
              direction="column"
              justify="space-between"
              alignItems="center"
            >
              <Grid item container>
                {tab === 0 && <MainTabView />}
                {tab === 1 && <AdvancedOptionsView />}
                {tab === 2 && <PlotsView />}
                {tab === 3 && <ModelsView />}
              </Grid>
              <Grid item container className={classes.execContainer}>
                <Grid item xs={12} className={classes.divider}>
                  <Divider />
                </Grid>
                <Grid item container justify="center" spacing={0}>
                  <Grid item container justify="center" xs={12}>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExec}
                        fullWidth
                        style={{ minWidth: "10vw" }}
                        disabled={isComputing || isXPSEmpty}
                      >
                        {isSpinnerHidden && <span>Executar</span>}
                        {!isSpinnerHidden && (
                          <Grid item>
                            <CircularProgress color="secondary" size="1.3em" />
                          </Grid>
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={classes.divider}>
                    <Divider />
                  </Grid>

                  <Grid item container direction="column" justify="flex-end">
                    <Grid item container xs justify="center">
                      <Grid item xs="auto">
                        <Typography variant="body1">
                          msanalyzer {version} -&nbsp;
                        </Typography>
                      </Grid>
                      <Grid item xs="auto">
                        <Link
                          href="https://github.com/marcusbfs/msanalyzer"
                          variant="body1"
                        >
                          GitHub
                        </Link>
                      </Grid>
                    </Grid>
                    <Grid item container xs justify="center">
                      <Grid item xs="auto">
                        <Typography variant="body2">{email}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        ) : (
          <div>
            <LoadingServerView />
          </div>
        )}
      </ThemeProvider>
    </React.Fragment>
  );
};

export default App;
